import ListOfFilesWithIncludeExclude from 'components/common/Deal/ListOfFilesWithIncludeExclude';
import { CodeNameModel } from 'components/common/types';
import { calculateSecondaryPanelPosition } from 'components/pages/Planner/PlannerLineActions/calculateSecondaryPanelPosition';
import PlannerSecondaryPanel from 'components/pages/Planner/PlannerSecondaryPanel';
import FrameFileUploadButton from 'components/common/Deal/FrameFileUploadButton/FrameFileUploadButton';
import { SectionButtonTheme } from 'lib/SectionButton/SectionButton.types';
import { cloneDeep, isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import usePrevious from 'customHooks/usePrevious';
import { updateIsObjectiveSubsectionChange } from 'store/objective/reducer';
import { useFrameListSearch } from 'components/common/Deal/hooks/useFrameListSearch';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { findSelectedRouteFrameCode, updateFrameSelection } from 'utils/frameList';
import FetchedOptionsCollapsible from 'components/common/Deal/FetchedOptionsCollapsible/FetchedOptionsCollapsible';
import SelectedOptionsCollapsible from 'components/common/Deal/SelectedOptionsCollapsible/SelectedOptionsCollapsible';
import Search from 'lib/Search';
import { Store } from 'components/common/types/Store.types';
import { FrameListProps } from './FrameList.types';
import FrameFilePasteArea from '../FrameFilePasteArea/FrameFilePasteArea';

const MAX_FRAME_FILE_UPLOAD = 5;

/* eslint-disable sonarjs/cognitive-complexity */
const FrameListPanel = ({
  initialSelectedRouteFrameCodes,
  selectedRouteFrameCodes,
  setSelectedRouteFrameCodes,
  initialMapSelectedFrames,
  mapRouteFrameCodes,
  setMapSelectedFrames,
  initialUploadedListOfFiles,
  uploadedListOfFiles,
  setUploadedListOfFiles,
  onConfirm,
  onCancel,
  onRemoveFile,
  cancelButtonLabel,
  confirmButtonLabel,
  maxFileRows,
  isSubsection = false,
  isIncludeExcludeFrameInUploadedFileDisabled = false,
  localSearchOptions,
  excludedSearchOptions,
}: FrameListProps): React.ReactElement => {
  const dispatch = useDispatch();
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);
  const environmentId = useSelector((state: Store) => state.environment.environmentId);

  const {
    fetchedOptions,
    searchText,
    setSearchText,
    openCategories,
    onToggleCategory,
    isOptionIncluded,
    isOptionSelected,
  } = useFrameListSearch(selectedRouteFrameCodes, localSearchOptions, excludedSearchOptions);

  const onOptionIncludeExcludeChange = (value: CodeNameModel): void => {
    const frameCodes = cloneDeep(selectedRouteFrameCodes);

    if (!frameCodes) return;

    const updatedFrames = updateFrameSelection(frameCodes, value);

    setSelectedRouteFrameCodes(updatedFrames);
  };

  const isMapOptionIncluded = (code: string, mediaOwner?: string): boolean | undefined => {
    if (!mapRouteFrameCodes) return undefined;

    const foundSelectedFiles = findSelectedRouteFrameCode(mapRouteFrameCodes, code, mediaOwner);

    if (!foundSelectedFiles) {
      return undefined;
    }

    return foundSelectedFiles?.include || false;
  };

  const isMapOptionSelected = (code: string, mediaOwner: string): boolean => {
    if (!mapRouteFrameCodes) return false;

    return !!findSelectedRouteFrameCode(mapRouteFrameCodes, code, mediaOwner);
  };

  const onMapOptionIncludeExcludeChange = (value: CodeNameModel): void => {
    if (!setMapSelectedFrames || !mapRouteFrameCodes) return;

    const frameCodes = cloneDeep(mapRouteFrameCodes);
    const updatedFrames = updateFrameSelection(frameCodes, value);

    setMapSelectedFrames(updatedFrames);
  };

  const prevInitialRouteFrameCodes = usePrevious(initialSelectedRouteFrameCodes);
  const prevInitialListFiles = usePrevious(initialUploadedListOfFiles);

  useEffect(() => {
    if (
      prevInitialRouteFrameCodes !== undefined &&
      !isEqual(prevInitialRouteFrameCodes, initialSelectedRouteFrameCodes) &&
      !isEqual(initialSelectedRouteFrameCodes, selectedRouteFrameCodes)
    ) {
      setSelectedRouteFrameCodes(initialSelectedRouteFrameCodes);
    }
  }, [initialSelectedRouteFrameCodes, selectedRouteFrameCodes, setSelectedRouteFrameCodes, prevInitialRouteFrameCodes]);

  useEffect(() => {
    if (
      prevInitialListFiles !== undefined &&
      !isEqual(prevInitialListFiles, initialUploadedListOfFiles) &&
      !isEqual(initialUploadedListOfFiles, uploadedListOfFiles)
    ) {
      setUploadedListOfFiles(initialUploadedListOfFiles);
    }
  }, [initialUploadedListOfFiles, uploadedListOfFiles, setUploadedListOfFiles, prevInitialListFiles]);

  const isChange = useMemo(
    () =>
      !isEqual(initialSelectedRouteFrameCodes, selectedRouteFrameCodes) ||
      !isEqual(initialUploadedListOfFiles, uploadedListOfFiles) ||
      !isEqual(initialMapSelectedFrames, mapRouteFrameCodes),
    [
      initialSelectedRouteFrameCodes,
      selectedRouteFrameCodes,
      initialUploadedListOfFiles,
      uploadedListOfFiles,
      initialMapSelectedFrames,
      mapRouteFrameCodes,
    ],
  );

  useEffect(() => {
    if (isSubsection) dispatch(updateIsObjectiveSubsectionChange(isChange));
  }, [isChange, isSubsection]);

  return (
    <PlannerSecondaryPanel
      title="Frame List"
      onSubmit={onConfirm}
      onCancel={onCancel}
      cancelButtonLabel={cancelButtonLabel}
      confirmButtonLabel={confirmButtonLabel}
      isDisabled={!isChange}
      isSubsection={isSubsection}
      showBackButton={isSubsection}
      isChange={isChange}
      customStyles={calculateSecondaryPanelPosition({ width: 400 })}
      pendoId="Frame List"
    >
      <div className="mb-6">
        <div className="mb-2">
          <Search value={searchText} placeholder="Search route frame codes" onChange={setSearchText} />
        </div>

        {isDirectSalesCampaignType && fetchedOptions.length ? (
          <FetchedOptionsCollapsible
            fetchedOptions={fetchedOptions}
            onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
            isOptionIncluded={isOptionIncluded}
            isOptionSelected={isOptionSelected}
            onToggleCategory={onToggleCategory}
            openCategories={openCategories}
          />
        ) : null}

        {searchText?.length >= 3 && !fetchedOptions.length ? (
          <p className="text-center px-5 py-2.5 sub-header-base">No results found</p>
        ) : null}

        <div className="mb-2">
          <FrameFileUploadButton
            listFiles={initialUploadedListOfFiles}
            uploadedListOfFiles={uploadedListOfFiles}
            setUploadedListOfFiles={setUploadedListOfFiles}
            theme={SectionButtonTheme.DASHED}
            isFileUploadVisible
            maxFileUploads={MAX_FRAME_FILE_UPLOAD}
            maxFileRows={maxFileRows}
          />
        </div>

        {uploadedListOfFiles.length ? (
          <ListOfFilesWithIncludeExclude
            uploadedListOfFiles={uploadedListOfFiles}
            setUploadedListOfFiles={setUploadedListOfFiles}
            onToggleCategory={onToggleCategory}
            openCategories={openCategories}
            onRemoveFile={onRemoveFile}
            isIncludeExcludeDisabled={isIncludeExcludeFrameInUploadedFileDisabled}
          />
        ) : null}

        <div className="mb-2">
          <FrameFilePasteArea
            environmentId={environmentId}
            selectedRouteFrameCodes={selectedRouteFrameCodes}
            setSelectedRouteFrameCodes={setSelectedRouteFrameCodes}
          />
        </div>

        <SelectedOptionsCollapsible
          setSelectedRouteFrameCodes={setSelectedRouteFrameCodes}
          onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
          isOptionIncluded={isOptionIncluded}
          isOptionSelected={isOptionSelected}
          selectedRouteFrameCodes={selectedRouteFrameCodes}
          onToggleCategory={onToggleCategory}
          openCategories={openCategories}
          label="Selected"
        />

        {isDirectSalesCampaignType && !isSubsection && mapRouteFrameCodes && setMapSelectedFrames ? (
          <SelectedOptionsCollapsible
            setSelectedRouteFrameCodes={setMapSelectedFrames}
            onOptionIncludeExcludeChange={onMapOptionIncludeExcludeChange}
            isOptionIncluded={isMapOptionIncluded}
            isOptionSelected={isMapOptionSelected}
            selectedRouteFrameCodes={mapRouteFrameCodes}
            onToggleCategory={onToggleCategory}
            openCategories={openCategories}
            label="Map Selected"
            isExcludeHidden
          />
        ) : null}

        {!isDirectSalesCampaignType && fetchedOptions.length ? (
          <FetchedOptionsCollapsible
            fetchedOptions={fetchedOptions}
            onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
            isOptionIncluded={isOptionIncluded}
            isOptionSelected={isOptionSelected}
            onToggleCategory={onToggleCategory}
            openCategories={openCategories}
            shouldStartOpen
          />
        ) : null}
      </div>
    </PlannerSecondaryPanel>
  );
};

export default FrameListPanel;
/* eslint-enable sonarjs/cognitive-complexity */
